import { useState } from "react";
import { Box, Button, Flex, Text } from "@simplybusiness/mobius";
import {
  PromptInput,
  PromptInputTextarea,
  PromptInputSubmit,
} from "@simplybusiness/mobius-chat-ui";
import type { ShowcaseProduct } from "../../../pages/ShowcasePage/types";
import { useInsuranceAdvisor } from "../../../contexts/InsuranceAdvisorProvider";
import shieldIcon from "../../../assets/insurance_advisor_shield.png";

export type QuickReplyQuestion = {
  label: string;
  value: string;
};

export type ShowcaseEmbeddedQuickRepliesProps = {
  /** Title for the quick replies section */
  title?: string;
  /** Placeholder text for the input field */
  inputPlaceholder?: string;
  /** Product data for Insurance Advisor integration */
  product: ShowcaseProduct;
  /** Callback when a quick reply button is clicked */
  onQuestionClick?: (question: QuickReplyQuestion) => void;
  /** Callback when a custom question is submitted */
  onQuestionSubmit?: (question: string) => void;
};

export const ShowcaseEmbeddedQuickReplies = ({
  title = "Frequently asked questions",
  inputPlaceholder = "Write your question",
  product,
  onQuestionClick,
  onQuestionSubmit,
}: ShowcaseEmbeddedQuickRepliesProps) => {
  const [inputValue, setInputValue] = useState("");
  const { getAdvisorAttributes } = useInsuranceAdvisor();

  const validatedInsuranceAdvisorData = getAdvisorAttributes(
    product.insuranceAdvisorProductCode,
  );

  const QUESTIONS = [
    {
      label: `What's covered with ${product.productDisplayName}?`,
      value: `What is covered by ${product.productDisplayName}?`,
    },
    {
      label: `Why might I need ${product.productDisplayName}?`,
      value: `Why do I need ${product.productDisplayName}?`,
    },
    {
      label: "What's typically not covered?",
      value: `What's not covered by ${product.productDisplayName}?`,
    },
    {
      label: "What coverage limits are available?",
      value: `What coverage limits do you offer for ${product.productDisplayName} and what does it depend on?`,
    },
  ];

  if (!validatedInsuranceAdvisorData) {
    return null;
  }

  const handleQuestionClick = (question: QuickReplyQuestion) => {
    onQuestionClick?.(question);
  };

  const handleSubmit = () => {
    if (inputValue.trim()) {
      onQuestionSubmit?.(inputValue.trim());
      setInputValue("");
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
  };

  return (
    <Box className="mobius-showcase-embedded-quick-replies">
      <Flex
        className="mobius-showcase-embedded-quick-replies__header"
        alignItems="center"
        gap="xs"
      >
        <img
          src={shieldIcon}
          alt=""
          className="mobius-showcase-embedded-quick-replies__shield-icon"
          aria-hidden="true"
          data-testid="quick-replies-shield-icon"
        />
        <Text
          elementType="h3"
          className="mobius-showcase-embedded-quick-replies__title"
        >
          {title}
        </Text>
      </Flex>

      <Flex
        className="mobius-showcase-embedded-quick-replies__questions"
        gap="xs"
        flexWrap="wrap"
      >
        {QUESTIONS.map(question => (
          <Button
            key={question.value}
            type="button"
            variant="ghost"
            size="sm"
            className="mobius-showcase-embedded-quick-replies__question-button sb-iaw reco-agent-ai-button"
            onClick={() => handleQuestionClick(question)}
            {...validatedInsuranceAdvisorData}
            data-value={question.value}
          >
            {question.label}
          </Button>
        ))}
      </Flex>

      <Box className="mobius-showcase-embedded-quick-replies__input-container">
        <PromptInput
          onSubmit={handleSubmit}
          showDisclaimer={false}
          noAnimation={true}
          className="reco-agent-ai-button sb-iaw-form"
          {...validatedInsuranceAdvisorData}
        >
          <PromptInputTextarea
            value={inputValue}
            onChange={handleInputChange}
            placeholder={inputPlaceholder}
          />
          <PromptInputSubmit disabled={!inputValue.trim()} />
        </PromptInput>
      </Box>
    </Box>
  );
};
